/** external */
import _ from "lodash";
import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import { Dialog } from "@toddle-edu/ds-web";

/** internal */
import withLoader from "UIComponents/GqlWithLoader";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import withPusherBind from "UIComponents/PusherConsumerHoc";
import I18nHOC from "UIComponents/I18nHOC";
import OfflineOverlayHOC from "UIComponents/OfflineOverlayHOC";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import {
  getUserType,
  getCurrentCurriculumProgramType,
  getSelectedAcademicYearId,
} from "modules/CommonSelectors";
import {
  TODDLE_LIBRARY as ACTIVE_TAB_TODDLE_LIBRARY,
  WORKSHEET_QUESTION_CREATION,
} from "Constants/stringConstants";
import { getGradeBasicDetailsFromCache } from "modules/CommonGraphqlHelpers";
import { getIsStudentAssignmentRevampEnabled } from "ClassRoom/modules/utils";

/** relative */
import {
  getAssessmentQuestionsQueryVariables,
  getAssessmentSubmissionQueryVariables,
  getDummyWorksheetSubmission,
  getMultiSelectDropdownOptions,
  getAssignmentSubjects,
  isToddleLibraryEnabledForWorksheets,
  getAssessmentDetailsData,
  isValidRevamped,
  getErrorCount,
  getErrorCountV2,
  getDummyWorksheetSubmissionV2,
} from "./modules/WorksheetUtils";
import {
  isWorksheetQueriesV2Enabled,
  getIsToddleLibraryWorksheetQueriesV2Enabled,
} from "./modules/WorksheetFeatureFlags";
import {
  WORKSHEET_MODES,
  MODE_TEACHER_VIEW,
  MODE_TEACHER_EVALUATE,
  MODE_TEACHER_CREATE,
  MODE_STUDENT_ATTEMPT,
  MODE_STUDENT_EVALUATE,
  MODE_STUDENT_VIEW,
  MODE_TEACHER_PREVIEW,
  MODE_ASSESSMENT_VIEW,
  TODDLE_LIBRARY,
  WORKSHEET_ELEMENT_IDS,
  GENERATE_USING_AI,
  EXTRACT_FROM_DOC,
  AI_GEN_IDENTIFIER,
} from "./modules/WorksheetConstants";
import {
  updateParentEntity,
  resetState,
  updateActiveQuestion,
  disableScrollEvent,
  setErrorState,
  updateQuestionLibraryTags,
  updateQuestionLibraryDefaultFilters,
  updateQuestionLibraryType,
  throttledCreateWorksheetSubmissionWrapper,
  updateParentSubmissionId,
  updateExtractFromDoc,
  updateWorksheetSidebar,
  updateAIAssessmentTools,
} from "./modules/WorksheetModules";
import StudentWorksheetSubmission from "./components/StudentWorksheetSubmission";
import TeacherEvaluation from "./components/TeacherEvaluation";
import withPusherSubscribe from "UIComponents/PusherSubscribeHoc";
import {
  getAssessmentQuestionsEnhancer,
  getStudentAssignmentQuestionSubmissionsEnhancer,
  getAssessmentQuestionsEnhancerV2,
} from "./modules/WorksheetEnhancers";
import ViewWorksheet from "./components/ViewWorksheet";
import classes from "./Worksheet.scss";
import CreateWorksheetV2 from "./components/CreateWorksheetV2";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";

class Worksheet extends PureComponent {
  constructor(props) {
    super(props);
    props?.setAssessmentDetailsRef?.(this);
    props?.mode === MODE_TEACHER_CREATE ? props?.setWorksheetRef?.(this) : null;

    this.state = {
      showInvalidQuestionsModal: false,
      noQuestionsModalState: { isOpen: false, isCreateMode: false },
    };

    // Adding debounce for smoother update of active questions
    this.handleScroll = _.debounce(this.handleScroll.bind(this), 25);

    this.worksheetContainerRef = null;
    this.scrollContainerRef = null;
    this.questionRefs = {};
  }

  componentDidMount = () => {
    const {
      updateParentEntity,
      parentId,
      parentType,
      mode,
      courseGrades,
      updateQuestionLibraryTags,
      updateQuestionLibraryDefaultFilters,
      classSubjects,
      updateQuestionLibraryType,
      isToddleLibraryPortal,
      globalGradeUid,
      curriculumType,
      submissionId,
      updateParentSubmissionId,
    } = this.props;

    updateParentSubmissionId(submissionId);

    if (mode == MODE_TEACHER_CREATE) {
      this.worksheetContainerRef?.addEventListener?.(
        "click",
        this.onWorksheetClick
      );
    }

    if (
      mode == MODE_TEACHER_CREATE &&
      !isToddleLibraryPortal &&
      isToddleLibraryEnabledForWorksheets({
        curriculumType,
        globalGradeUid,
      })
    ) {
      // Toddle library should be the default library type if it is enabled
      updateQuestionLibraryType(TODDLE_LIBRARY);
    }

    if (mode == MODE_TEACHER_CREATE) {
      const updatedCourseGrades = _.map(courseGrades, grade => {
        return _.isString(grade) ? getGradeBasicDetailsFromCache(grade) : grade;
      });

      updateQuestionLibraryTags({
        grades: getMultiSelectDropdownOptions(updatedCourseGrades),
        subjects: getMultiSelectDropdownOptions(classSubjects),
      });

      updateQuestionLibraryDefaultFilters([
        {
          key: "selectedGrades",
          value: _.map(updatedCourseGrades, grade => ({
            id: _.get(grade, "id"),
          })),
        },
        {
          key: "selectedSubjects",
          value: _.map(classSubjects, subject => ({
            id: _.get(subject, "id"),
          })),
        },
      ]);
    }

    if (
      !_.includes(
        [MODE_TEACHER_CREATE, MODE_TEACHER_EVALUATE, MODE_TEACHER_VIEW],
        mode
      )
    ) {
      this.scrollContainerRef?.addEventListener?.("scroll", this.handleScroll);

      this.scrollContainerRef?.addEventListener?.(
        "scrollend",
        this.handleScrollEnd
      );
    }

    updateParentEntity({
      id: parentId,
      type: parentType,
      submissionId,
    });

    if (_.isEqual(mode, MODE_TEACHER_CREATE)) {
      /** Toggle error states in question if they are invalid
       * we are using setTimeout because the question components are not mounted yet
       */
      setTimeout(() => {
        this.setWorksheetErrorStateRevamped();
      });
    }
  };

  componentWillUnmount = () => {
    const { resetState, mode, updateActiveQuestion } = this.props;

    if (mode == MODE_TEACHER_CREATE) {
      this.worksheetContainerRef?.removeEventListener?.(
        "click",
        this.onWorksheetClick
      );
    }

    if (
      !_.includes(
        [MODE_TEACHER_CREATE, MODE_TEACHER_EVALUATE, MODE_TEACHER_VIEW],
        mode
      )
    ) {
      this.scrollContainerRef?.removeEventListener?.(
        "scroll",
        this.handleScroll
      );

      this.scrollContainerRef?.removeEventListener?.(
        "scrollend",
        this.handleScrollEnd
      );
    }

    if (mode != MODE_TEACHER_PREVIEW) {
      resetState();
    } else if (mode === MODE_TEACHER_PREVIEW) {
      updateActiveQuestion(WORKSHEET_QUESTION_CREATION);
    }
  };

  componentDidUpdate = prevProps => {
    const { mode } = this.props;
    if (
      mode === MODE_TEACHER_CREATE &&
      prevProps.activeQuestion !== this.props.activeQuestion
    ) {
      this.setWorksheetErrorStateRevamped();
    }
  };

  handleScroll = () => {
    const {
      updateActiveQuestion,
      activeQuestion,
      isScrollEventDisabled,
    } = this.props;

    if (!isScrollEventDisabled) {
      let activeQuestionInView = false;

      _.forEach(this.questionRefs, (questionRef, questionId) => {
        const questionDiv = _.get(
          questionRef,
          "current.containerRef.current",
          null
        );
        if (questionDiv && questionId === activeQuestion) {
          const rect = questionDiv.getBoundingClientRect();
          const viewportHeight =
            window.innerHeight || document.documentElement.clientHeight;
          if (
            (rect.top >= 0 && rect.top < viewportHeight) ||
            (rect.bottom > 0 && rect.bottom <= viewportHeight) ||
            (rect.top < 0 && rect.bottom > viewportHeight)
          ) {
            activeQuestionInView = true;
          }
        }
      });

      if (!activeQuestionInView) {
        updateActiveQuestion(null);
      }
    }
  };

  handleScrollEnd = () => {
    const { disableScrollEvent } = this.props;

    disableScrollEvent(false);
  };

  onWorksheetClick = e => {
    const {
      updateActiveQuestion,
      activeQuestion,
      mode,
      progressOfUploads,
      updateAIAssessmentTools,
    } = this.props;

    const clickedElement = e?.target;

    // Check if the clicked element toggles a question
    const questionToggleElement = clickedElement?.closest(
      "#active-question-toggle"
    );

    // Check if the clicked element is inside the active question card
    const questionCardElement = clickedElement?.closest(
      "#active-question-card"
    );

    // * Worksheet sidebar collapse/Expand detection
    const worksheetSidebarCollapse = clickedElement?.closest(
      `#${WORKSHEET_ELEMENT_IDS.WORKSHEET_SIDEBAR_COLLAPSE_ID}`
    );
    const isFromWorksheetSidebarCollapse = Boolean(worksheetSidebarCollapse);

    // * Worksheet question assistant detection
    const questionAssistant = clickedElement?.closest(
      `#${WORKSHEET_ELEMENT_IDS.WORKSHEET_QUESTION_ASSISTANT_ID}`
    );
    const isFromQuestionAssistant = Boolean(questionAssistant);

    // * Worksheet Add Question dropdown detection
    const addQuestionDropdown = clickedElement?.closest(
      `#${WORKSHEET_ELEMENT_IDS.WORKSHEET_ADD_QUESTION_DROPDOWN_ID}`
    );
    const isFromAddQuestionDropdown = Boolean(addQuestionDropdown);

    // * Worksheet Add Question In Between detection
    const addQuestionInBetween = clickedElement?.closest(
      `#${WORKSHEET_ELEMENT_IDS.WORKSHEET_ADD_QUESTION_IN_BETWEEN_ID}`
    );
    const isFromAddQuestionInBetween = Boolean(addQuestionInBetween);

    // * Worksheet question assistant detection
    const assessmentTools = clickedElement?.closest(
      `#${WORKSHEET_ELEMENT_IDS.WORKSHEET_AI_ASSESSMENT_TOOLS_ID}`
    );
    const isAIAssessmentTools = Boolean(assessmentTools);

    // * Worksheet Assessment tool and Basic Details detection
    const isNotQuestionEntity = _.includes(
      [WORKSHEET_QUESTION_CREATION, GENERATE_USING_AI, EXTRACT_FROM_DOC],
      activeQuestion
    );

    // * Checking is Ai question generating
    const isStartingAiGen = _.startsWith(activeQuestion, AI_GEN_IDENTIFIER);

    // * Not background click for worksheet sidebar collapse, add question dropdown
    if (
      isStartingAiGen ||
      isNotQuestionEntity ||
      isFromAddQuestionDropdown ||
      isFromWorksheetSidebarCollapse ||
      isFromQuestionAssistant ||
      isFromAddQuestionInBetween ||
      isAIAssessmentTools
    ) {
      return;
    }

    // Deselect a question in create mode, when -
    //  there is an active question and user has not clicked inside active question
    //  there are no uploads in progress
    //  user has not clicked on a button that toggles another question (as that function itself will call updateActiveQuestion)
    const shouldDeselectQuestion =
      mode == MODE_TEACHER_CREATE &&
      activeQuestion &&
      _.isNil(questionCardElement) &&
      _.isEmpty(progressOfUploads);

    if (shouldDeselectQuestion) {
      if (_.isNil(questionToggleElement)) {
        updateActiveQuestion(null);
        // close AI assessment tools assistant when clicked outside the assistant
        updateAIAssessmentTools({ isOpen: false });
      }

      this.setWorksheetErrorState();
    }
  };

  setWorksheetErrorState = () => {
    const { setErrorState, data, errorState, isQueriesV2Enabled } = this.props;

    const questionIds = [];

    // Get question data
    if (isQueriesV2Enabled) {
      _.forEach(data, section => {
        _.get(section, "children", []).forEach(questionEdge => {
          questionIds.push(_.get(questionEdge, "item.id", null));

          _.get(questionEdge, "children", []).forEach(subQuestionEdge => {
            questionIds.push(_.get(subQuestionEdge, "item.id", null));
          });
        });
      });
    } else {
      //WORKSHEET_RESTRUCTURING:cleanup else block when queriesV2 is enabled
      _.forEach(data, section => {
        _.get(section, "childrenV2.edges", []).forEach(questionEdge => {
          questionIds.push(_.get(questionEdge, "node.item.id", null));

          _.get(questionEdge, "node.childrenV2.edges", []).forEach(
            subQuestionEdge => {
              questionIds.push(_.get(subQuestionEdge, "node.item.id", null));
            }
          );
        });
      });
    }

    const newErrorStates = {};
    _.forEach(this.questionRefs, questionRef => {
      const questionErrorState = questionRef?.current?.toggleErrorState?.();

      const { questionId = null, value = false } = questionErrorState || {};
      if (questionId && _.includes(questionIds, questionId)) {
        newErrorStates[questionId] = value;
      }
    });

    if (!_.isEqual(errorState, newErrorStates)) {
      setErrorState(newErrorStates);
    }

    return newErrorStates;
  };

  setWorksheetErrorStateRevamped = (checkAllQuestions = false) => {
    const {
      setErrorState,
      data,
      activeQuestion,
      errorState,
      isGeneratingAIQuestions,
      isQueriesV2Enabled,
    } = this.props;

    const questionData = [];
    const questionIds = [];

    // Get question data
    // We are using this function to set error states for questions in the worksheet
    if (isQueriesV2Enabled) {
      _.forEach(data, section => {
        _.get(section, "children", []).forEach(questionEdge => {
          const question = _.get(questionEdge, "item", null);
          if (!_.isEmpty(question)) {
            questionData.push(question);
            questionIds.push(_.get(question, "id"));
          }

          _.get(questionEdge, "children", []).forEach(subQuestionEdge => {
            const subQuestion = _.get(subQuestionEdge, "item", null);
            if (!_.isEmpty(subQuestion)) {
              questionData.push(subQuestion);
              questionIds.push(_.get(subQuestion, "id"));
            }
          });
        });
      });
    } else {
      //WORKSHEET_RESTRUCTURING:cleanup else block when queriesV2 is enabled
      _.forEach(data, section => {
        _.get(section, "childrenV2.edges", []).forEach(questionEdge => {
          const question = _.get(questionEdge, "node.item", null);
          if (!_.isEmpty(question)) {
            questionData.push(question);
            questionIds.push(_.get(question, "id"));
          }

          _.get(questionEdge, "node.childrenV2.edges", []).forEach(
            subQuestionEdge => {
              const subQuestion = _.get(subQuestionEdge, "node.item", null);
              if (!_.isEmpty(subQuestion)) {
                questionData.push(subQuestion);
                questionIds.push(_.get(subQuestion, "id"));
              }
            }
          );
        });
      });
    }

    const filteredErrorStates = _.pickBy(errorState, (value, key) =>
      _.includes(questionIds, key)
    );

    const newErrorStates = filteredErrorStates || [];

    // check for errors
    _.forEach(questionData, question => {
      const questionId = _.get(question, "id");

      if (
        !_.get(errorState, questionId, null) &&
        !isGeneratingAIQuestions &&
        (checkAllQuestions || activeQuestion !== questionId)
      ) {
        const currentFields = _.concat(
          _.get(question, "fields", []),
          _.get(question, "answerFields", [])
        );
        const errorCount = isValidRevamped(currentFields);
        newErrorStates[questionId] = !!errorCount;
      }
    });

    // update error states
    if (!_.isEqual(errorState, newErrorStates)) {
      setErrorState(newErrorStates);
    }

    return newErrorStates;
  };

  isWorksheetValid = params => {
    const { checkForInvalidQuestions = false, isCreateMode = false } =
      params || {};

    const {
      setToastMsg,
      data,
      mode,
      activeQuestion,
      isQueriesV2Enabled,
    } = this.props;

    if (mode !== MODE_TEACHER_CREATE) {
      return true;
    }

    let newErrorStates = {};
    // 1. Deselect any active questions
    if (activeQuestion || checkForInvalidQuestions) {
      newErrorStates = this.setWorksheetErrorStateRevamped(true);
    }

    // 2. if there are no questions in any of the sections, return false
    const isSectionsEmpty = _.every(data, section => {
      //WORKSHEET_RESTRUCTURING:Use children when queriesV2 is enabled
      const key = isQueriesV2Enabled ? "children" : "childrenV2.edges";
      return _.isEmpty(_.get(section, key, []));
    });
    if (isSectionsEmpty) {
      if (mode === MODE_TEACHER_CREATE) {
        if (checkForInvalidQuestions) {
          this.toggleNoQuestionsModal(isCreateMode);
        } else {
          return true;
        }
      } else {
        setToastMsg({
          msg: `toastMsgs:label_cannot_be_emty`,
          locale_params: [
            { key: "label", value: "common:section", isPlainText: false },
          ],
        });
      }
      return false;
    }

    // 3. When assign button is clicked, show a warning message if there are invalid questions
    if (checkForInvalidQuestions) {
      const errCount = _.countBy(newErrorStates, Boolean)?.true || 0;

      if (errCount > 0) {
        this.toggleInvalidQuestionsModal();
        return false;
      }
    }

    return true;
  };

  toggleInvalidQuestionsModal = () => {
    const { updateWorksheetSidebar } = this.props;
    const { showInvalidQuestionsModal } = this.state;
    if (showInvalidQuestionsModal) {
      // * Showing error only when closing the modal
      updateWorksheetSidebar({ showErrors: true });
    }

    this.setState(prevState => ({
      showInvalidQuestionsModal: !prevState.showInvalidQuestionsModal,
    }));
  };

  toggleNoQuestionsModal = (isCreateMode = false) => {
    this.setState(prevState => {
      const isOpen = _.get(prevState, "noQuestionsModalState.isOpen");

      return {
        ...prevState,
        noQuestionsModalState: { isOpen: !isOpen, isCreateMode },
      };
    });
  };

  handleDoneClick = () => {
    const { onWorksheetDoneClick } = this.props;

    this.toggleInvalidQuestionsModal();
    onWorksheetDoneClick?.();
  };

  setQuestionRef = (ref, questionId) => {
    this.questionRefs[questionId] = ref;
  };

  setWorksheetContainerRef = ref => {
    this.worksheetContainerRef = ref;
  };

  setScrollContainerRef = ref => {
    this.scrollContainerRef = ref;
  };

  renderModeWiseView = () => {
    const {
      data,
      submissions,
      mode,
      worksheetInstructions,
      parentId,
      parentType,
      toggleWorksheetModal,
      onSubmitWork,
      setWorksheetWebViewRecentlySubmitted,
      assignmentId,
      handleWorksheetAssessmentToolClick,
      isSubmitted,
      userType,
      parentResourceId,
      isFromLibrary,
      showWorksheetResumeModal,
      toggleWorksheetResumeModal,
      defaultQuestionIds,
      showWorksheetAssistant,
      isToddleLibraryPortal,
      assessmentDetailsComponent,
      totalQuestionAttempted,
      worksheetDetailsRef,
      sourceFrom,
      classSubjects,
      creationMode,
      courseId,
      selectedCourse,
      unitPlanId,
      isFromUnitPlan,
      isDraft,
      assessmentQuestionsQueryVariables,
      studentAssignmentId,
      studentAssignmentDetailQueryVariables,
      parentItemId,
      t,
      showAssessmentToolsInQuestions,
      assessmentQuestionsQueryVariablesV2,
      isQueriesV2Enabled,
      isFromToddleLibrary,
      refetchStudentAssignmentQuestionSubmissions,
      refetchStudentAssignmentDetails,
    } = this.props;

    const shouldRenderWorksheet = !(
      userType == "parent" && mode == MODE_STUDENT_ATTEMPT
    );

    switch (mode) {
      case MODE_TEACHER_CREATE: {
        return (
          <CreateWorksheetV2
            mode={mode}
            data={data}
            parentId={parentId}
            parentType={parentType}
            setQuestionRef={this.setQuestionRef}
            setWorksheetContainerRef={this.setWorksheetContainerRef}
            parentResourceId={parentResourceId}
            defaultQuestionIds={defaultQuestionIds}
            showWorksheetAssistant={showWorksheetAssistant}
            assessmentDetailsComponent={assessmentDetailsComponent}
            worksheetDetailsRef={worksheetDetailsRef}
            sourceFrom={sourceFrom}
            classSubjects={classSubjects}
            creationMode={creationMode}
            courseId={courseId}
            selectedCourse={selectedCourse}
            unitPlanId={unitPlanId}
            isFromUnitPlan={isFromUnitPlan}
            isDraft={isDraft}
            assignmentId={assignmentId}
            isToddleLibraryPortal={isToddleLibraryPortal}
            parentItemId={parentItemId}
            isQueriesV2Enabled={isQueriesV2Enabled}
          />
        );
      }

      case MODE_ASSESSMENT_VIEW:
        return (
          <ViewWorksheet
            mode={mode}
            data={data}
            setQuestionRef={this.setQuestionRef}
            setWorksheetContainerRef={this.setWorksheetContainerRef}
            setScrollContainerRef={this.setScrollContainerRef}
            isFromLibrary={isFromLibrary}
            isToddleLibraryPortal={isToddleLibraryPortal}
            assignmentId={assignmentId}
            isQueriesV2Enabled={
              isFromToddleLibrary
                ? getIsToddleLibraryWorksheetQueriesV2Enabled()
                : isQueriesV2Enabled
            }
            isFromToddlePortal={isFromToddleLibrary}
            worksheetInstructions={worksheetInstructions}
          />
        );
      case MODE_TEACHER_EVALUATE:
      case MODE_TEACHER_VIEW:
        return (
          <TeacherEvaluation
            data={data}
            mode={mode}
            submissions={submissions}
            setQuestionRef={this.setQuestionRef}
            setWorksheetContainerRef={this.setWorksheetContainerRef}
            setScrollContainerRef={this.setScrollContainerRef}
            isSubmitted={isSubmitted}
            assignmentId={assignmentId}
            isQueriesV2Enabled={isQueriesV2Enabled}
          />
        );

      case MODE_STUDENT_ATTEMPT:
      case MODE_STUDENT_VIEW:
      case MODE_STUDENT_EVALUATE:
        return shouldRenderWorksheet ? (
          <StudentWorksheetSubmission
            mode={mode}
            submissions={submissions}
            assignmentId={assignmentId}
            assessmentId={parentId}
            onSubmitWork={onSubmitWork}
            setWorksheetWebViewRecentlySubmitted={
              setWorksheetWebViewRecentlySubmitted
            }
            toggleWorksheetModal={toggleWorksheetModal}
            handleWorksheetAssessmentToolClick={
              handleWorksheetAssessmentToolClick
            }
            setQuestionRef={this.setQuestionRef}
            setWorksheetContainerRef={this.setWorksheetContainerRef}
            setScrollContainerRef={this.setScrollContainerRef}
            showWorksheetResumeModal={showWorksheetResumeModal}
            toggleWorksheetResumeModal={toggleWorksheetResumeModal}
            totalQuestionAttempted={totalQuestionAttempted}
            assessmentQuestionsQueryVariables={
              assessmentQuestionsQueryVariables
            }
            parentType={parentType}
            studentAssignmentId={studentAssignmentId}
            studentAssignmentDetailQueryVariables={
              studentAssignmentDetailQueryVariables
            }
            t={t}
            showAssessmentToolsInQuestions={showAssessmentToolsInQuestions}
            assessmentQuestionsQueryVariablesV2={
              assessmentQuestionsQueryVariablesV2
            }
            isQueriesV2Enabled={isQueriesV2Enabled}
            refetchStudentAssignmentQuestionSubmissions={
              refetchStudentAssignmentQuestionSubmissions
            }
            refetchStudentAssignmentDetails={refetchStudentAssignmentDetails}
          />
        ) : (
          toggleWorksheetModal() // to close worksheet if teacher has requested for resubmission and worksheet is open in family end
        );
      case MODE_TEACHER_PREVIEW: {
        //WORKSHEET_RESTRUCTURING: Use getAssessmentQuestionsEnhancerV2 when queriesV2 is enabled
        const _getDummyWorksheetSubmission = isQueriesV2Enabled
          ? getDummyWorksheetSubmissionV2
          : getDummyWorksheetSubmission;
        const dummySubmissions = _getDummyWorksheetSubmission({
          sections: data,
        });
        return (
          <StudentWorksheetSubmission
            mode={mode}
            data={data}
            submissions={dummySubmissions}
            assignmentId={assignmentId}
            assessmentId={parentId}
            toggleWorksheetModal={toggleWorksheetModal}
            setQuestionRef={this.setQuestionRef}
            setWorksheetContainerRef={this.setWorksheetContainerRef}
            setScrollContainerRef={this.setScrollContainerRef}
            totalQuestionAttempted={totalQuestionAttempted}
            t={t}
            isQueriesV2Enabled={isQueriesV2Enabled}
            worksheetInstructions={worksheetInstructions}
          />
        );
      }
      default:
        return null;
    }
  };

  render() {
    const {
      showFullScreenLoader,
      errorState,
      data,
      t,
      isQueriesV2Enabled,
    } = this.props;

    const { showInvalidQuestionsModal, noQuestionsModalState } = this.state;

    const showNoQuestionsModal = _.get(noQuestionsModalState, "isOpen", false);
    const isAssignmentCreateMode = _.get(
      noQuestionsModalState,
      "isCreateMode",
      false
    );
    //WORKSHEET_RESTRUCTURING: Use getErrorCountV2 when queriesV2 is enabled
    const _getErrorCount = isQueriesV2Enabled ? getErrorCountV2 : getErrorCount;
    const errorsCount = _getErrorCount(data, errorState);

    return (
      <>
        {this.renderModeWiseView()}
        {showInvalidQuestionsModal && errorsCount > 0 ? (
          <Dialog
            dsVersion="2.0"
            testId={"worksheet-invalid-questions"}
            variant="confirm-dialog"
            title={t("worksheet:incomplete_questions")}
            message={t("worksheet:incomplete_questions_subtext_with_count", {
              count: errorsCount,
            })}
            buttonOneText={t("common:cancel")}
            buttonTwoText={t("common:confirm")}
            onButtonOneClick={this.toggleInvalidQuestionsModal}
            onButtonTwoClick={this.handleDoneClick}
            onClose={this.toggleInvalidQuestionsModal}
          />
        ) : null}
        {showNoQuestionsModal ? (
          <Dialog
            dsVersion="2.0"
            testId={"worksheet-noQuestions"}
            variant="delete-dialog"
            title={
              isAssignmentCreateMode
                ? t("worksheet:assign_worksheet")
                : t("common:update_with_label", {
                    label: t("common:worksheet"),
                  })
            }
            message={
              isAssignmentCreateMode
                ? t("worksheet:no_questions_error_prompt")
                : t("worksheet:no_questions_error_prompt2")
            }
            buttonOneText={t("common:cancel")}
            buttonTwoText={
              isAssignmentCreateMode
                ? t("worksheet:assign_anyway")
                : t("common:update")
            }
            onButtonOneClick={this.toggleNoQuestionsModal}
            onButtonTwoClick={this.handleDoneClick}
            onClose={this.toggleNoQuestionsModal}
          />
        ) : null}
        {showFullScreenLoader ? (
          <FullScreenLoader overlayContainer={classes.overlayContainer} />
        ) : null}
      </>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const showFullScreenLoader = _.get(state, "worksheet.isLoading", false);
  const isScrollEventDisabled = _.get(
    state,
    "worksheet.isScrollEventDisabled",
    false
  );

  const curriculumType = getCurrentCurriculumProgramType(state);

  const {
    parentId,
    parentType,
    submissionId,
    mode,
    courseSubjects,
    portalType,
    studentAssignmentId,
    importFromToddleLibrary,
    assessmentTitle: assessmentTitleFromProps,
    worksheetInstructions: worksheetInstructionsFromProps,
  } = ownProps;
  const userType = getUserType(state);

  // show assessment tools at the question level
  const showAssessmentToolsInQuestions = _.includes(
    [MODE_STUDENT_ATTEMPT, MODE_STUDENT_EVALUATE, MODE_STUDENT_VIEW],
    mode
  );

  const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
    {
      parentId,
      parentType,
      portalType,
      userType,
      mode,
      showAssessmentToolsInQuestions,
    }
  );

  const assessmentQuestionsQueryVariablesV2 = {
    id: parentId,
    type: parentType,
    portalType,
  };

  const assessmentSubmissionQueryVariables = getAssessmentSubmissionQueryVariables(
    {
      submissionId: submissionId,
      mode: mode,
    }
  );

  const isStudentAssignmentRevampEnabled = getIsStudentAssignmentRevampEnabled();

  const studentAssignmentDetailQueryVariables = {
    id: studentAssignmentId,
    userFilter: "ALL",
    entityEventsFilter: {
      orderBy: "EVENT_TIME",
      orderByDir: "ASC",
    },
  };
  if (!isStudentAssignmentRevampEnabled) {
    studentAssignmentDetailQueryVariables.showOnlySubmitted = false;
  }

  const activeQuestion = _.get(state, "worksheet.activeQuestion", null);

  const progressOfUploads = _.get(state, "app_services.progressOfUploads", {});

  const academicYearId = getSelectedAcademicYearId(state);
  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };

  const assignmentSubjects = getAssignmentSubjects({
    assessmentId: parentId,
    collaboratorsFilter,
  });
  const classSubjects = !_.isEmpty(assignmentSubjects)
    ? assignmentSubjects
    : courseSubjects;

  const errorState = _.get(state, "worksheet.errorState");
  const isGeneratingAIQuestions = _.get(
    state,
    "worksheet.generatingAIQuestions",
    false
  );

  const isToddleLibraryPortal =
    state.login.activeTab === ACTIVE_TAB_TODDLE_LIBRARY;

  const gradeId = _.get(state, "teacher.selected_class.selected_grades[0]", "");
  let globalGradeUid;
  if (gradeId) {
    const gradeDetails = getGradeBasicDetailsFromCache(gradeId);
    globalGradeUid = _.get(gradeDetails, "globalGrade.uid", null);
  }

  let { assessmentTitle, worksheetInstructions } = getAssessmentDetailsData({
    assessmentId: parentId,
    collaboratorsFilter,
  });
  if (_.isEmpty(assessmentTitle) && isToddleLibraryPortal) {
    assessmentTitle = assessmentTitleFromProps || "";
  }
  if (_.isEmpty(worksheetInstructions) && isToddleLibraryPortal) {
    worksheetInstructions = worksheetInstructionsFromProps || "";
  }

  // This is used to determine if the new query structure should be applied
  // V2 queries should be used (feature flag enabled AND not in Toddle Library)
  const isQueriesV2Enabled = isWorksheetQueriesV2Enabled(
    importFromToddleLibrary
  );
  const userInfoForPusher = isToddleLibraryPortal
    ? getUserInfo({ portalType: "PLANNER" })
    : null;
  const pusherData = userInfoForPusher
    ? { organizationId: userInfoForPusher.org_id, userId: userInfoForPusher.id }
    : null;

  return {
    isData: true,
    isLoading: false,
    assessmentQuestionsQueryVariables,
    assessmentSubmissionQueryVariables,
    showFullScreenLoader,
    activeQuestion,
    pusherData,
    progressOfUploads,
    userType,
    isScrollEventDisabled,
    errorState,
    isGeneratingAIQuestions,
    classSubjects,
    isToddleLibraryPortal,
    globalGradeUid,
    curriculumType,
    studentAssignmentDetailQueryVariables,
    assessmentTitle,
    showAssessmentToolsInQuestions,
    collaboratorsFilter,
    assessmentQuestionsQueryVariablesV2,
    worksheetInstructions,
    isQueriesV2Enabled,
  };
};

const mapActionCreators = {
  updateParentEntity,
  resetState,
  updateActiveQuestion,
  setToastMsg,
  disableScrollEvent,
  setErrorState,
  updateQuestionLibraryTags,
  updateQuestionLibraryDefaultFilters,
  updateQuestionLibraryType,
  throttledCreateWorksheetSubmissionWrapper,
  updateParentSubmissionId,
  updateExtractFromDoc,
  updateWorksheetSidebar,
  updateAIAssessmentTools,
};

export default compose(
  I18nHOC({ resource: ["common", "worksheet"] }),
  connect(mapStateToProps, mapActionCreators),
  getAssessmentQuestionsEnhancer(),
  getStudentAssignmentQuestionSubmissionsEnhancer(),
  getAssessmentQuestionsEnhancerV2(),
  withLoader,
  withPusherBind([
    "studentAssignmentSubmitted",
    "assignmentFeedUpdate",
    "questionExtractionJob",
  ]),
  OfflineOverlayHOC({ showOverlay: true }),
  withPusherSubscribe(["user"])
)(Worksheet);

Worksheet.propTypes = {
  mode: PropTypes.oneOf(WORKSHEET_MODES),
  parentId: PropTypes.string.isRequired,
  parentType: PropTypes.string.isRequired,
  contentType: PropTypes.string,
  assessmentId: PropTypes.string,
  submissionId: PropTypes.string,
  isSubmitted: PropTypes.bool,
  userType: PropTypes.string,
  courseGrades: PropTypes.array,
  onWorksheetDoneClick: PropTypes.func,
  isFromLibrary: PropTypes.bool,
  showWorksheetResumeModal: PropTypes.bool,
  toggleWorksheetResumeModal: PropTypes.func,
  courseSubjects: PropTypes.array,
  classSubjects: PropTypes.array,
  isFromToddleLibrary: PropTypes.bool,
  showWorksheetAssistant: PropTypes.bool,
  assessmentDetailsComponent: PropTypes.element,
  totalQuestionAttempted: PropTypes.number,
  sourceFrom: PropTypes.string,
  creationMode: PropTypes.string,
  courseId: PropTypes.string,
  selectedCourse: PropTypes.object,
  unitPlanId: PropTypes.string,
  isFromUnitPlan: PropTypes.bool,
  isDraft: PropTypes.bool,
  isQueriesV2Enabled: PropTypes.bool,
  importFromToddleLibrary: PropTypes.bool,
};

Worksheet.defaultProps = {
  mode: MODE_TEACHER_VIEW,
  isFromLibrary: false,
  isFromToddleLibrary: false,
  showWorksheetAssistant: false,
  selectedCourse: {},
  isFromUnitPlan: false,
  isDraft: false,
  isQueriesV2Enabled: false,
  data: [],
  importFromToddleLibrary: false,
};
